/**
 * App-level listener that turns background-session chat finals into per-case
 * unread state (agent-bus P6 item 3).
 *
 * One gateway socket carries every session's frames; `useChatSession`
 * deliberately drops the ones not addressed to its own sessionKey (and
 * DEV-tallies them as `sessionKey-mismatch`). This hook is the other consumer
 * of exactly those frames: a `state:"final"` for a CASE session that is not
 * the active chat surface means the case gained a line nobody watched — the
 * ⚖ D-8 shape, where a bus-origin concierge turn lands up to ~65 s after the
 * mission ends, typically after the lawyer moved on.
 *
 * Counts FINALS only (one turn = one unread; deltas would count a single turn
 * dozens of times). Mounted ONCE in `AppShell`, inside `GatewayProvider`.
 */
import { useCallback } from "react";
import { useEventStream } from "@/hooks/useEventStream";
import { useChatUnreadStore } from "@/stores/useChatUnreadStore";
import { parseLxSessionKey } from "@/types/session";
import type { ChatEventPayloadRaw } from "@/types/gateway";

export function useChatUnreadTracker(): void {
  useEventStream(
    "chat",
    useCallback((payload: unknown) => {
      const raw = payload as ChatEventPayloadRaw;
      if (raw?.state !== "final") return;
      if (!raw.sessionKey) return;
      const { activeSessionKey, noteArrival } = useChatUnreadStore.getState();
      // The mounted surface renders its own finals live — never unread.
      if (raw.sessionKey === activeSessionKey) return;
      const ctx = parseLxSessionKey(raw.sessionKey);
      // Per-CASE affordance only (the P6 item): global/client/matter/sub-scope
      // threads are out of scope v1 and stay untracked rather than half-shown.
      if (!ctx || ctx.type !== "case" || !ctx.caseId) return;
      noteArrival(ctx.caseId);
    }, []),
  );
}
